// src/BubbleMenu.tsx
import {
  createEffect,
  createSignal,
  onCleanup
} from "solid-js";
import {
  BubbleMenuPlugin
} from "@tiptap/extension-bubble-menu";
var BubbleMenu = (props) => {
  const [ref, setRef] = createSignal();
  const stableEditorRef = props.editor;
  createEffect(() => {
    const element = ref();
    if (!element) {
      return;
    }
    const pluginKey = props.pluginKey || "bubbleMenu";
    const tippyOptions = props.tippyOptions || {};
    const shouldShow = props.shouldShow || null;
    stableEditorRef.registerPlugin(
      BubbleMenuPlugin({
        pluginKey,
        editor: stableEditorRef,
        element,
        tippyOptions,
        shouldShow
      })
    );
    onCleanup(() => {
      stableEditorRef.unregisterPlugin(pluginKey);
    });
  });
  return <div ref={setRef} class={props.class} style={{ visibility: "hidden" }}>
			{props.children}
		</div>;
};

// src/createEditor.ts
import { Editor } from "@tiptap/core";
import { createSignal as createSignal2, onCleanup as onCleanup2, onMount, getOwner } from "solid-js";

// src/ReactiveOwner.ts
var ReactiveOwnerProperty = Symbol(
  "Reactive owner property used by tiptap solid as a workaround"
);
var getTiptapSolidReactiveOwner = (editor) => editor[ReactiveOwnerProperty] ?? void 0;

// src/createEditor.ts
var createEditor = (options) => {
  const [editor, setEditor] = createSignal2(null, {
    equals: false
  });
  const owner = getOwner();
  onMount(() => {
    const instance = new Editor({
      ...options,
      onBeforeCreate(props) {
        props.editor[ReactiveOwnerProperty] = owner;
        options.onBeforeCreate?.(props);
      }
    });
    instance.on("transaction", () => {
      requestAnimationFrame(() => {
        setEditor(instance);
      });
    });
    onCleanup2(() => {
      editor()?.destroy();
    });
  });
  return editor;
};

// src/EditorContent.tsx
import {
  createEffect as createEffect2,
  onCleanup as onCleanup3,
  splitProps
} from "solid-js";
var EditorContent = (props) => {
  const [local, rest] = splitProps(props, ["editor"]);
  let editorContentRef;
  let mounted = false;
  createEffect2(() => {
    const { editor } = local;
    if (editor?.options.element) {
      if (mounted) return;
      mounted = true;
      editorContentRef.append(...editor.options.element.childNodes);
      editor.setOptions({
        element: editorContentRef
      });
      editor.createNodeViews();
    }
  });
  onCleanup3(() => {
    const { editor } = local;
    if (!editor) {
      return;
    }
    if (!editor.isDestroyed) {
      editor.view.setProps({
        nodeViews: {}
      });
    }
    if (!editor.options.element.firstChild) {
      return;
    }
    const newElement = document.createElement("div");
    newElement.append(...editor.options.element.childNodes);
    editor.setOptions({
      element: newElement
    });
  });
  return <div
    ref={editorContentRef}
    {...rest}
  />;
};

// src/FloatingMenu.tsx
import { createEffect as createEffect3, onCleanup as onCleanup4 } from "solid-js";
import {
  FloatingMenuPlugin
} from "@tiptap/extension-floating-menu";
var FloatingMenu = (props) => {
  let element = null;
  createEffect3(() => {
    if (!element) {
      return;
    }
    const {
      pluginKey = "floatingMenu",
      editor,
      tippyOptions = {},
      shouldShow = null
    } = props;
    editor.registerPlugin(
      FloatingMenuPlugin({
        pluginKey,
        editor,
        element,
        tippyOptions,
        shouldShow
      })
    );
    onCleanup4(() => {
      editor.unregisterPlugin(pluginKey);
    });
  });
  return <div
    ref={(e) => element = e}
    class={props.class}
    style={{ visibility: "hidden" }}
  >
      {props.children}
    </div>;
};

// src/NodeViewContent.tsx
import { Dynamic } from "solid-js/web";

// src/useSolidNodeView.ts
import { createContext, useContext } from "solid-js";
var SolidNodeViewContext = createContext({
  onDragStart: void 0
});
var useSolidNodeView = () => useContext(SolidNodeViewContext);

// src/NodeViewContent.tsx
var NodeViewContent = (props) => {
  const state = useSolidNodeView();
  return <Dynamic
    component={props.as || "div"}
    {...props}
    ref={state.nodeViewContentRef}
    data-node-view-content=""
    style={{
      ...props.style,
      "white-space": "pre-wrap"
    }}
  />;
};

// src/NodeViewRenderer.tsx
import {
  NodeView
} from "@tiptap/core";
import { createRoot } from "solid-js";
import { createStore } from "solid-js/store";
import { Dynamic as Dynamic2, insert } from "solid-js/web";
var SolidNodeView = class extends NodeView {
  rootElement;
  contentElement;
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  setProps;
  dispose;
  constructor(component, props, options) {
    super(component, props, options);
    createRoot((dispose) => {
      this.dispose = dispose;
      const [props2, setProps] = createStore({
        editor: this.editor,
        node: this.node,
        decorations: this.decorations,
        selected: false,
        extension: this.extension,
        getPos: () => this.getPos(),
        updateAttributes: (attributes = {}) => this.updateAttributes(attributes),
        deleteNode: () => this.deleteNode()
      });
      this.setProps = setProps;
      const tagName = this.node.isInline ? "span" : "div";
      this.rootElement = document.createElement(tagName);
      this.rootElement.classList.add("solid-renderer");
      this.contentElement = this.node.isLeaf ? null : document.createElement(tagName);
      if (this.contentElement) {
        this.contentElement.style.whiteSpace = "inherit";
      }
      const SolidNodeViewProvider = (componentProps) => {
        const onDragStart = this.onDragStart.bind(this);
        const nodeViewContentRef = (element) => {
          if (element && this.contentElement && element.firstChild !== this.contentElement) {
            element.appendChild(this.contentElement);
          }
        };
        return <SolidNodeViewContext.Provider
          value={{ onDragStart, nodeViewContentRef }}
        >
            <Dynamic2 component={this.component} {...componentProps} />
          </SolidNodeViewContext.Provider>;
      };
      insert(this.rootElement, SolidNodeViewProvider(props2));
    }, getTiptapSolidReactiveOwner(this.editor));
  }
  get dom() {
    if (!this.rootElement?.firstElementChild?.hasAttribute(
      "data-node-view-wrapper"
    )) {
      throw Error(
        "Please use the NodeViewWrapper component for your node view."
      );
    }
    return this.rootElement;
  }
  get contentDOM() {
    if (this.node.isLeaf) {
      return null;
    }
    return this.contentElement;
  }
  update(node, decorations) {
    if (typeof this.options.update === "function") {
      const oldNode = this.node;
      const oldDecorations = this.decorations;
      this.node = node;
      this.decorations = decorations;
      return this.options.update(oldNode, oldDecorations);
    }
    if (node.type !== this.node.type) {
      return false;
    }
    if (node === this.node && this.decorations === decorations) {
      return true;
    }
    this.node = node;
    this.decorations = decorations;
    this.setProps({ node, decorations });
    return true;
  }
  selectNode() {
    this.setProps({
      selected: true
    });
  }
  deselectNode() {
    this.setProps({
      selected: false
    });
  }
  destroy() {
    if (this.rootElement) this.rootElement.textContent = "";
    this.dispose();
    this.contentElement = null;
    this.rootElement = null;
  }
};
function SolidNodeViewRenderer(component, options) {
  return (props) => {
    return new SolidNodeView(
      component,
      props,
      options
    );
  };
}

// src/NodeViewWrapper.tsx
import { Dynamic as Dynamic3 } from "solid-js/web";
var NodeViewWrapper = (props) => {
  const { onDragStart } = useSolidNodeView();
  return <Dynamic3
    component={props.as || "div"}
    {...props}
    data-node-view-wrapper=""
    onDragStart={onDragStart}
    style={{
      ...props.style,
      whiteSpace: "normal"
    }}
  />;
};

// src/SolidRenderer.tsx
import { createRoot as createRoot2 } from "solid-js";
import { createStore as createStore2 } from "solid-js/store";
import { Dynamic as Dynamic4, insert as insert2 } from "solid-js/web";
var SolidRenderer = class {
  id;
  editor;
  element;
  dispose;
  setProps;
  constructor(component, { editor, props, as = "div", className = "" }) {
    this.id = Math.floor(Math.random() * 4294967295).toString();
    this.editor = editor;
    this.element = document.createElement(as);
    this.element.classList.add("solid-renderer");
    createRoot2((dispose) => {
      const [reactiveProps, setProps] = createStore2(
        props ?? {}
      );
      this.setProps = setProps;
      if (className) {
        this.element.classList.add(...className.split(" "));
      }
      insert2(
        this.element,
        <Dynamic4 component={component} {...reactiveProps} />
      );
      this.dispose = dispose;
    }, getTiptapSolidReactiveOwner(this.editor));
  }
  updateProps(props) {
    this.setProps({
      ...props
    });
  }
  destroy() {
    this.dispose();
    this.element.remove();
  }
};
export {
  BubbleMenu,
  EditorContent,
  FloatingMenu,
  NodeViewContent,
  NodeViewWrapper,
  SolidNodeView,
  SolidNodeViewContext,
  SolidNodeViewRenderer,
  SolidRenderer,
  createEditor,
  getTiptapSolidReactiveOwner,
  useSolidNodeView
};
